import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Heart, Search, Loader2, Moon, Brain, Dumbbell, Apple, Droplets,
  RefreshCw, ChevronDown, ChevronUp, Flame, Footprints, BedDouble, User,
  MessageSquare, Flag, CalendarDays, Send, AlertTriangle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';
import API from '../lib/api';

const MOODS = { happy: 'bg-emerald-100 text-emerald-700', calm: 'bg-blue-100 text-blue-700', neutral: 'bg-slate-100 text-slate-600', anxious: 'bg-amber-100 text-amber-700', stressed: 'bg-orange-100 text-orange-700', sad: 'bg-rose-100 text-rose-700' };

export default function DoctorHealthTrackingPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterType, setFilterType] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showTargets, setShowTargets] = useState(false);
  const [targetSearch, setTargetSearch] = useState('');
  const [targetPatients, setTargetPatients] = useState([]);
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetForm, setTargetForm] = useState({});
  const [targetPatientId, setTargetPatientId] = useState(null);
  const [targetSaving, setTargetSaving] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, limit: 500 };
      if (filterType) params.log_type = filterType;
      if (selectedPatient) params.patient_id = selectedPatient;
      const res = await API.get('/health-tracking/logs', { params });
      setLogs(res.data.logs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [dateFrom, dateTo, filterType, selectedPatient]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Fetch compliance alerts
  useEffect(() => {
    (async () => {
      try { const res = await API.get('/health-tracking/compliance-alerts'); setAlerts(res.data.alerts || []); } catch {}
    })();
  }, [logs]);

  const searchTargetPatients = async (q) => {
    setTargetSearch(q);
    setTargetLoading(true);
    try {
      const res = await API.get(`/health-tracking/doctor-patients?search=${encodeURIComponent(q)}`);
      setTargetPatients(res.data.patients || []);
    } catch {}
    setTargetLoading(false);
  };

  const openTargets = () => { setShowTargets(true); searchTargetPatients(''); };

  const saveTargets = async () => {
    if (!targetPatientId) return;
    setTargetSaving(true);
    try {
      await API.post('/health-tracking/recommendations', { patient_id: targetPatientId, ...targetForm });
      toast.success('Targets saved'); setTargetPatientId(null); setTargetForm({}); searchTargetPatients(targetSearch);
    } catch { toast.error('Failed'); }
    setTargetSaving(false);
  };

  // Group by patient
  const patients = useMemo(() => {
    const map = {};
    logs.forEach(l => {
      const pid = l.patient_id;
      if (!map[pid]) map[pid] = { id: pid, logs: [], name: '' };
      map[pid].logs.push(l);
    });
    return Object.values(map);
  }, [logs]);

  // Resolve patient names
  const [patientNames, setPatientNames] = useState({});
  useEffect(() => {
    const ids = patients.map(p => p.id).filter(id => !patientNames[id]);
    if (ids.length === 0) return;
    (async () => {
      try {
        const res = await API.get(`/health-tracking/patient-names?ids=${ids.join(',')}`);
        setPatientNames(prev => ({ ...prev, ...(res.data.names || {}) }));
      } catch {}
    })();
  }, [patients]);

  const filteredPatients = patients.filter(p => {
    if (!search) return true;
    const name = (patientNames[p.id] || p.id).toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="doctor-health-tracking">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Patient Health Tracking</h1>
          <p className="text-sm text-slate-500">Sleep, stress, activity & nutrition logs from your patients</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200"><CardContent className="p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search patient..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="ht-search" />
          </div>
          <div className="grid grid-cols-2 sm:flex gap-2 items-center flex-wrap w-full sm:w-auto">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-full sm:w-36 text-sm" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-full sm:w-36 text-sm" />
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-xs">
              <option value="">All Types</option>
              <option value="sleep">Sleep</option>
              <option value="stress">Stress</option>
              <option value="activity">Activity</option>
              <option value="nutrition">Nutrition</option>
            </select>
          </div>
        </div>
      </CardContent></Card>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-indigo-700">{logs.filter(l => l.type === 'sleep').length}</p>
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Moon className="w-3 h-3" /> Sleep Entries</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{logs.filter(l => l.type === 'stress').length}</p>
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Brain className="w-3 h-3" /> Mood Entries</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{logs.filter(l => l.type === 'activity').length}</p>
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Dumbbell className="w-3 h-3" /> Activities</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-orange-600">{logs.filter(l => l.type === 'nutrition').length}</p>
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Apple className="w-3 h-3" /> Meals</p>
        </CardContent></Card>
      </div>

      {/* Set Targets button */}
      <Button onClick={openTargets} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="set-targets-btn">
        <Send className="w-4 h-4 mr-1.5" /> Set Health Targets for Patient
      </Button>

      {/* Compliance Alerts */}
      {alerts.length > 0 && (
        <Card className="border-rose-200 bg-rose-50" data-testid="compliance-alerts">
          <CardContent className="p-3 space-y-2">
            <p className="text-sm font-bold text-rose-700 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Compliance Alerts</p>
            {alerts.map((a, i) => (
              <div key={i} className="bg-white rounded-lg border border-rose-200 p-2.5">
                <p className="text-xs font-semibold text-slate-800">{a.patient_name}</p>
                {a.issues.map((issue, j) => (
                  <p key={j} className="text-[11px] text-rose-600 mt-0.5 flex items-center gap-1"><Flag className="w-3 h-3" />{issue}</p>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Patient cards */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" /></div>
      ) : filteredPatients.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><Heart className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No health tracking data found</p></div>
      ) : (
        <div className="space-y-4">
          {filteredPatients.map(p => <PatientCard key={p.id} patient={p} name={patientNames[p.id] || p.id.slice(0, 8)} onRefresh={fetchLogs} />)}
        </div>
      )}

      {/* Set Targets Dialog */}
      <Dialog open={showTargets} onOpenChange={setShowTargets}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Send className="w-4 h-4 text-indigo-600" />Set Health Targets</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search patient by name, phone, or ID..." value={targetSearch} onChange={e => searchTargetPatients(e.target.value)} className="pl-9 h-9" data-testid="target-patient-search" />
            </div>
            {targetLoading ? <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin text-teal-600 mx-auto" /></div> : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {targetPatients.map(p => (
                  <button key={p.patient_id || p.clinic_patient_id} onClick={() => { setTargetPatientId(p.patient_id); setTargetForm(p.targets || {}); }}
                    className={`w-full text-left p-2 rounded-lg border text-xs ${targetPatientId === p.patient_id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-teal-300'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-800">{p.name || 'Unknown'}</span>
                      {p.has_targets && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Has Targets</Badge>}
                    </div>
                    <span className="text-slate-500">{p.phone}</span>
                  </button>
                ))}
                {targetPatients.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No patients found</p>}
              </div>
            )}
            {targetPatientId && (
              <div className="border-t pt-3 space-y-3">
                <p className="text-xs font-bold text-slate-600">Set Targets</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Daily Calories</Label><Input type="number" value={targetForm.daily_calories_intake||''} onChange={e => setTargetForm({...targetForm, daily_calories_intake: parseInt(e.target.value)||null})} placeholder="2000" className="h-9 mt-0.5" /></div>
                  <div><Label className="text-xs">Water (glasses)</Label><Input type="number" value={targetForm.daily_water_glasses||''} onChange={e => setTargetForm({...targetForm, daily_water_glasses: parseInt(e.target.value)||null})} placeholder="8" className="h-9 mt-0.5" /></div>
                  <div><Label className="text-xs">Daily Steps</Label><Input type="number" value={targetForm.daily_steps||''} onChange={e => setTargetForm({...targetForm, daily_steps: parseInt(e.target.value)||null})} placeholder="10000" className="h-9 mt-0.5" /></div>
                  <div><Label className="text-xs">Sleep Hours</Label><Input type="number" step="0.5" value={targetForm.sleep_hours||''} onChange={e => setTargetForm({...targetForm, sleep_hours: parseFloat(e.target.value)||null})} placeholder="8" className="h-9 mt-0.5" /></div>
                </div>
                <div><Label className="text-xs">Restrictions</Label><Input value={targetForm.restrictions||''} onChange={e => setTargetForm({...targetForm, restrictions: e.target.value})} placeholder="e.g. Low sodium, No sugar" className="h-9 mt-0.5" /></div>
                <div><Label className="text-xs">Notes</Label><Input value={targetForm.notes||''} onChange={e => setTargetForm({...targetForm, notes: e.target.value})} placeholder="Additional instructions" className="h-9 mt-0.5" /></div>
                <Button onClick={saveTargets} disabled={targetSaving} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">{targetSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save Targets</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PatientCard({ patient, name, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [noteDialog, setNoteDialog] = useState(null); // log object
  const [noteText, setNoteText] = useState('');
  const [noteFlag, setNoteFlag] = useState('');
  const [recDialog, setRecDialog] = useState(false);
  const [rec, setRec] = useState({});
  const [saving, setSaving] = useState(false);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [calDays, setCalDays] = useState([]);
  const [showCal, setShowCal] = useState(false);

  const logs = patient.logs;
  const sleepLogs = logs.filter(l => l.type === 'sleep');
  const stressLogs = logs.filter(l => l.type === 'stress');
  const activityLogs = logs.filter(l => l.type === 'activity');
  const nutritionLogs = logs.filter(l => l.type === 'nutrition');

  const avgSleep = sleepLogs.length ? (sleepLogs.reduce((s, l) => s + (l.sleep_hours || 0), 0) / sleepLogs.length).toFixed(1) : '--';
  const avgStress = stressLogs.length ? (stressLogs.reduce((s, l) => s + (l.stress_level || 0), 0) / stressLogs.length).toFixed(1) : '--';
  const totalCals = nutritionLogs.reduce((s, l) => s + (l.calories || 0), 0);
  const totalSteps = activityLogs.reduce((s, l) => s + (l.steps || 0), 0);

  const sleepChart = [...sleepLogs].reverse().map(l => ({ d: l.date?.slice(5), h: l.sleep_hours }));
  const stressChart = [...stressLogs].reverse().map(l => ({ d: l.date?.slice(5), s: l.stress_level }));

  const addNote = async () => {
    if (!noteDialog || !noteText.trim()) return;
    setSaving(true);
    try {
      await API.post(`/health-tracking/logs/${noteDialog.id}/note`, { text: noteText, flag: noteFlag || null });
      toast.success('Note added'); setNoteDialog(null); setNoteText(''); setNoteFlag(''); onRefresh();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const saveRec = async () => {
    setSaving(true);
    try {
      await API.post('/health-tracking/recommendations', { patient_id: patient.id, ...rec });
      toast.success('Recommendation saved'); setRecDialog(false);
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const fetchCal = async (m) => {
    try {
      const res = await API.get(`/health-tracking/calendar?patient_id=${patient.id}&month=${m}`);
      setCalDays(res.data.days || []);
    } catch {}
  };

  useEffect(() => { if (showCal) fetchCal(calMonth); }, [showCal, calMonth]);

  const year = parseInt(calMonth.split('-')[0]);
  const mo = parseInt(calMonth.split('-')[1]) - 1;
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const firstDay = new Date(year, mo, 1).getDay();
  const dayMap = {}; calDays.forEach(d => { dayMap[d.date] = d; });

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <button className="w-full p-4 flex items-center justify-between text-left" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center"><User className="w-5 h-5 text-teal-700" /></div>
            <div>
              <p className="font-semibold text-sm text-slate-800">{name}</p>
              <p className="text-xs text-slate-500">{logs.length} entries</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3 text-xs">
              <span className="text-indigo-600"><BedDouble className="w-3 h-3 inline mr-0.5" />{avgSleep}h</span>
              <span className="text-amber-600"><Brain className="w-3 h-3 inline mr-0.5" />{avgStress}/10</span>
              <span className="text-emerald-600"><Footprints className="w-3 h-3 inline mr-0.5" />{totalSteps}</span>
              <span className="text-orange-600"><Flame className="w-3 h-3 inline mr-0.5" />{totalCals}cal</span>
            </div>
            {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </button>

        {expanded && (
          <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-4">
            {/* Action buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => setRecDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-[11px]"><Send className="w-3 h-3 mr-1" />Set Health Targets</Button>
              <Button size="sm" variant="outline" onClick={() => setShowCal(!showCal)} className="h-7 text-[11px]"><CalendarDays className="w-3 h-3 mr-1" />{showCal ? 'Hide' : 'Show'} Calendar</Button>
            </div>

            {/* Calendar */}
            {showCal && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => { const d = new Date(year, mo-1, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }} className="text-xs text-slate-500">&lt;</button>
                  <p className="text-xs font-bold text-slate-600">{new Date(year, mo).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
                  <button onClick={() => { const d = new Date(year, mo+1, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }} className="text-xs text-slate-500">&gt;</button>
                </div>
                <div className="grid grid-cols-7 gap-0.5 text-center text-[9px] text-slate-400 mb-1">
                  {['S','M','T','W','T','F','S'].map((d,i) => <div key={i}>{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const ds = `${calMonth}-${String(day).padStart(2, '0')}`;
                    const data = dayMap[ds];
                    const has = data && data.total > 0;
                    return (
                      <div key={day} className={`aspect-square rounded flex flex-col items-center justify-center text-[9px] ${has ? 'bg-teal-100 text-teal-800' : 'text-slate-400'}`}>
                        {day}
                        {has && <div className="flex gap-px">{data.sleep>0&&<div className="w-1 h-1 rounded-full bg-indigo-500"/>}{data.activity>0&&<div className="w-1 h-1 rounded-full bg-emerald-500"/>}{data.nutrition>0&&<div className="w-1 h-1 rounded-full bg-orange-500"/>}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Charts */}
            {sleepChart.length >= 2 && (
              <div><p className="text-xs font-bold text-slate-600 mb-1">Sleep Hours</p>
                <ResponsiveContainer width="100%" height={120}><BarChart data={sleepChart}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="d" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} /><Tooltip /><Bar dataKey="h" fill="#6366f1" radius={[3,3,0,0]} /></BarChart></ResponsiveContainer>
              </div>
            )}
            {stressChart.length >= 2 && (
              <div><p className="text-xs font-bold text-slate-600 mb-1">Stress Level</p>
                <ResponsiveContainer width="100%" height={120}><LineChart data={stressChart}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="d" tick={{ fontSize: 9 }} /><YAxis domain={[0,10]} tick={{ fontSize: 9 }} /><Tooltip /><Line type="monotone" dataKey="s" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} /></LineChart></ResponsiveContainer>
              </div>
            )}

            {/* Recent logs with note button */}
            <div>
              <p className="text-xs font-bold text-slate-600 mb-2">Recent Entries</p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {logs.slice(0, 30).map(l => (
                  <div key={l.id} className="py-1.5 border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className={`text-[9px] w-16 justify-center ${l.type === 'sleep' ? 'border-indigo-200 text-indigo-600' : l.type === 'stress' ? 'border-amber-200 text-amber-600' : l.type === 'activity' ? 'border-emerald-200 text-emerald-600' : 'border-orange-200 text-orange-600'}`}>{l.type}</Badge>
                      <span className="text-slate-500 w-16">{l.date}</span>
                      <span className="text-slate-800 flex-1">
                        {l.type === 'sleep' && `${l.sleep_hours}h (Q:${l.sleep_quality}/5)`}
                        {l.type === 'stress' && <><Badge className={`text-[9px] ${MOODS[l.mood] || ''}`}>{l.mood}</Badge> {l.stress_level}/10</>}
                        {l.type === 'activity' && `${l.activity_type} ${l.duration_minutes}min`}
                        {l.type === 'nutrition' && `${l.meal_type}: ${l.food_items?.slice(0,30)}`}
                      </span>
                      <button onClick={() => { setNoteDialog(l); setNoteText(''); setNoteFlag(''); }} className="text-slate-400 hover:text-teal-600" title="Add Note"><MessageSquare className="w-3.5 h-3.5" /></button>
                    </div>
                    {/* Show existing doctor notes */}
                    {l.doctor_notes?.map((n, ni) => (
                      <div key={ni} className={`ml-20 mt-1 text-[10px] px-2 py-1 rounded ${n.flag === 'warning' ? 'bg-rose-50 text-rose-700' : n.flag === 'important' ? 'bg-amber-50 text-amber-700' : n.flag === 'good' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'}`}>
                        <span className="font-medium">Dr. {n.doctor_name}: </span>{n.text}
                        {n.flag && <Badge className={`ml-1 text-[8px] ${n.flag === 'warning' ? 'bg-rose-200' : n.flag === 'important' ? 'bg-amber-200' : 'bg-emerald-200'}`}>{n.flag}</Badge>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Note Dialog */}
      <Dialog open={!!noteDialog} onOpenChange={v => { if (!v) setNoteDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><MessageSquare className="w-4 h-4 text-teal-600" />Add Clinical Note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Your clinical note..." className="h-9" />
            <div>
              <Label className="text-xs">Flag</Label>
              <div className="flex gap-2 mt-1">
                {[{v:'',l:'None'},{v:'good',l:'Good'},{v:'important',l:'Important'},{v:'warning',l:'Warning'}].map(f => (
                  <button key={f.v} onClick={() => setNoteFlag(f.v)} className={`px-2 py-1 rounded text-[10px] font-medium border ${noteFlag === f.v ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-500'}`}>{f.l}</button>
                ))}
              </div>
            </div>
            <Button onClick={addNote} disabled={saving || !noteText.trim()} className="w-full bg-teal-700 hover:bg-teal-800 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Add Note</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recommendation Dialog */}
      <Dialog open={recDialog} onOpenChange={setRecDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><AlertTriangle className="w-4 h-4 text-indigo-600" />Set Health Targets for {name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Daily Calories</Label><Input type="number" value={rec.daily_calories_intake||''} onChange={e => setRec({...rec, daily_calories_intake: parseInt(e.target.value)||null})} placeholder="2000" className="h-9 mt-0.5" /></div>
              <div><Label className="text-xs">Water (glasses)</Label><Input type="number" value={rec.daily_water_glasses||''} onChange={e => setRec({...rec, daily_water_glasses: parseInt(e.target.value)||null})} placeholder="8" className="h-9 mt-0.5" /></div>
              <div><Label className="text-xs">Daily Steps</Label><Input type="number" value={rec.daily_steps||''} onChange={e => setRec({...rec, daily_steps: parseInt(e.target.value)||null})} placeholder="10000" className="h-9 mt-0.5" /></div>
              <div><Label className="text-xs">Sleep Hours</Label><Input type="number" step="0.5" value={rec.sleep_hours||''} onChange={e => setRec({...rec, sleep_hours: parseFloat(e.target.value)||null})} placeholder="8" className="h-9 mt-0.5" /></div>
            </div>
            <div><Label className="text-xs">Dietary Restrictions</Label><Input value={rec.restrictions||''} onChange={e => setRec({...rec, restrictions: e.target.value})} placeholder="e.g. Low sodium, No sugar" className="h-9 mt-0.5" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={rec.notes||''} onChange={e => setRec({...rec, notes: e.target.value})} placeholder="Additional instructions" className="h-9 mt-0.5" /></div>
            <Button onClick={saveRec} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save Targets</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
